<template>
    <div class="ModelMetricsDocked q-pa-xs">
        <div class="row items-center q-mt-xs q-mb-sm">
            <div class="text-primary">
                {{ $t('visualization.modelMetrics.title') }}
            </div>
        </div>
        <div class="row q-col-gutter-sm">
            <div class="col-6 col-md-3 col-lg">
                <div class="KpiLabel">{{ $t('visualization.filters.contextualInfo.cases') }}</div>
                <q-card flat class="bg-grey-2">
                    <QLinearProgress
                        size="34px"
                        :value="casesRatio"
                        color="secondary"
                        trackColor="transparent"
                    >
                        <div class="absolute-full flex flex-center">
                            <QBadge color="white" textColor="secondary" class="flex flex-center">
                                <span class="q-px-sm text-center text-weight-bold text-caption text-black">
                                    {{ casesValue }}
                                </span>
                                {{ casesPct !== null ? `${casesPct}%` : '0%' }}
                                <QTooltip class="bg-gray-bg text-primary">
                                    {{ tooltipBase }}
                                </QTooltip>
                            </QBadge>
                        </div>
                    </QLinearProgress>
                </q-card>
            </div>

            <div class="col-6 col-md-3 col-lg">
                <div class="KpiLabel">{{ $t('visualization.filters.contextualInfo.variants') }}</div>
                <q-card flat class="bg-grey-2">
                    <QLinearProgress
                        size="34px"
                        :value="variantsRatio"
                        color="secondary"
                        trackColor="transparent"
                    >
                        <div class="absolute-full flex flex-center">
                            <QBadge color="white" textColor="secondary" class="flex flex-center">
                                <span class="q-px-sm text-center text-weight-bold text-caption text-black">
                                    {{ variantsValue }}
                                </span>
                                {{ variantsPct !== null ? `${variantsPct}%` : '0%' }}
                                <QTooltip class="bg-gray-bg text-primary">
                                    {{ tooltipBase }}
                                </QTooltip>
                            </QBadge>
                        </div>
                    </QLinearProgress>
                </q-card>
            </div>

            <div class="col-12 col-md-3 col-lg">
                <div class="KpiLabel">{{ $t('visualization.filters.contextualInfo.avg') }}</div>
                <q-card flat class="bg-grey-2">
                    <div class="q-pa-xs text-center text-weight-bold">
                        {{ avgCaseDurationFormatted }}
                    </div>
                </q-card>
            </div>

            <div class="col-12 col-md-3 col-lg">
                <div class="KpiLabel">{{ $t('visualization.filters.contextualInfo.sd') }}</div>
                <q-card flat class="bg-grey-2">
                    <div class="q-pa-xs text-center text-weight-bold">
                        {{ sdCaseDurationFormatted }}
                    </div>
                </q-card>
            </div>

            <div class="col-12 col-md-3 col-lg">
                <div class="KpiLabel">{{ $t('visualization.filters.contextualInfo.med') }}</div>
                <q-card flat class="bg-grey-2">
                    <div class="q-pa-xs text-center text-weight-bold">
                        {{ medCaseDurationFormatted }}
                    </div>
                </q-card>
            </div>

            <div class="col-6 col-md-3 col-lg">
                <div class="KpiLabel">{{ $t('visualization.filters.contextualInfo.min') }}</div>
                <q-card flat class="bg-grey-2">
                    <div class="q-pa-xs text-center text-weight-bold">
                        {{ minCaseDurationFormatted }}
                    </div>
                </q-card>
            </div>

            <div class="col-6 col-md-3 col-lg">
                <div class="KpiLabel">{{ $t('visualization.filters.contextualInfo.max') }}</div>
                <q-card flat class="bg-grey-2">
                    <div class="q-pa-xs text-center text-weight-bold">
                        {{ maxCaseDurationFormatted }}
                    </div>
                </q-card>
            </div>

            <div class="col-12 col-md-6 col-lg">
                <div class="KpiLabel">{{ $t('visualization.filters.contextualInfo.dateRangeTooltip') }}</div>
                <q-card flat class="bg-grey-2">
                    <div class="q-pa-xs text-center text-weight-bold">
                        {{ dateRangeFormatted }}
                    </div>
                </q-card>
            </div>
        </div>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import moment from 'moment'
import 'moment-duration-format'

export default {
    name: 'ModelMetricsDocked',
    inject: ['App'],
    props: {
        totalInfo: VueTypes.object.def({}),
        filteredInfo: VueTypes.object.def({}),
        displayedInfo: VueTypes.object.def({}),
        percentageMode: VueTypes.string, // 'TOTAL' | 'FILTERED'
    },
    computed: {
        tooltipBase() {
            return this.percentageMode === 'TOTAL'
                ? this.$t('visualization.filters.contextualInfo.total')
                : this.$t('visualization.filters.contextualInfo.filtered')
        },
        casesValue() {
            const v = this.displayedInfo?.cases
            return v === undefined || v === null ? '-' : v
        },
        variantsValue() {
            const v = this.displayedInfo?.variants
            return v === undefined || v === null ? '-' : v
        },
        casesPct() {
            return this.computePct('cases')
        },
        variantsPct() {
            return this.computePct('variants')
        },
        casesRatio() {
            return this.computeRatio('cases')
        },
        variantsRatio() {
            return this.computeRatio('variants')
        },
        avgCaseDurationFormatted() {
            return this.formatDurationKey('avg')
        },
        medCaseDurationFormatted() {
            return this.formatDurationKey('med')
        },
        sdCaseDurationFormatted() {
            return this.formatDurationKey('sd')
        },
        minCaseDurationFormatted() {
            return this.formatDurationKey('min')
        },
        maxCaseDurationFormatted() {
            return this.formatDurationKey('max')
        },
        dateRangeFormatted() {
            const start = this.displayedInfo?.minStartDateTime
            const end = this.displayedInfo?.maxEndDateTime
            if (!start || !end) return '-'

            const startFmt = this.App?.dateLocationFormat?.(start, 'short')
            const endFmt = this.App?.dateLocationFormat?.(end, 'short')
            if (!startFmt || !endFmt) return '-'
            if (startFmt === '1/1/1970' || endFmt === '1/1/1970') return '-'
            return `${startFmt} – ${endFmt}`
        },
    },
    methods: {
        computeRatio(key) {
            const shown = this.displayedInfo?.[key]
            if (typeof shown !== 'number') return 0

            const denom = this.percentageMode === 'TOTAL'
                ? this.totalInfo?.[key]
                : this.filteredInfo?.[key]

            if (typeof denom !== 'number' || denom <= 0) return 0
            return Math.max(0, Math.min(1, shown / denom))
        },
        computePct(key) {
            const shown = this.displayedInfo?.[key]
            if (typeof shown !== 'number') return null

            const denom = this.percentageMode === 'TOTAL'
                ? this.totalInfo?.[key]
                : this.filteredInfo?.[key]

            if (typeof denom !== 'number' || denom <= 0) return null
            return Math.round((shown / denom) * 100)
        },
        formatDurationKey(key) {
            const seconds = this.displayedInfo?.duration?.[key]
            if (typeof seconds !== 'number') return '-'
            return moment.duration(seconds, 'seconds').format('d[d] h[h]:m[m]', { largest: 2, trim: 'all' })
        },
    },
}
</script>

<style scoped lang="scss">
.KpiLabel {
    font-size: 0.75rem;
    color: $primary;
}
</style>
